import { css } from '@emotion/react';
import { Icon, IconColorType, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { FontColorType, Text, TextSize } from '@frontend/design-system';

export type IconWithTextProps = {
  icon?: IconName;
  iconSize?: number;
  placeholder?: string;
  text?: string;
  truncate?: boolean;
  width?: number | string;
  withDot?: boolean;
  size?: TextSize;
  textColor?: FontColorType;
  iconColor?: IconColorType;
};

export const IconWithText = ({
  icon,
  iconSize = 12,
  placeholder = '-',
  text,
  truncate = true,
  width = '100%',
  withDot = true,
  size = 'small',
  textColor,
  iconColor,
}: IconWithTextProps) => {
  const computedWidth = typeof width === 'number' ? `${width}px` : width;

  return (
    <div css={styles.container({ width: computedWidth })}>
      {icon ? (
        <Icon name={icon} color={iconColor} css={styles.icon(iconSize)} />
      ) : withDot ? (
        <span css={styles.dot(10)} />
      ) : null}

      <Text size={size} color={textColor} css={styles.text({ truncate, width: computedWidth })}>
        {text ?? placeholder}
      </Text>
    </div>
  );
};

const styles = {
  container: ({ width }: { width: string }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    overflow: hidden;
    width: ${width};
  `,

  icon: (size: number) => css`
    min-width: ${size}px;
    min-height: ${size}px;
    width: ${size}px;
    height: ${size}px;
  `,

  dot: (size: number) => css`
    background-color: ${theme.colors.warning50};
    border-radius: ${theme.borderRadius.full};
    min-width: ${size}px;
    min-height: ${size}px;
    width: ${size}px;
    height: ${size}px;
    margin-left: 1px;
  `,

  text: ({ truncate, width }: { truncate: boolean; width: string }) => css`
    width: ${width};
    ${truncate
      ? `
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      `
      : `
        overflow: visible;
        text-overflow: initial;
        white-space: normal;
      `}
  `,
};
